import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icons/Icon';
import { CompletedTrue, CompletedFalse } from '../Icons/Icons';
import tailwindTheme from '../../../../../config/tailwind.config';

const DragDropFiles = ({
  droppedFile,
  setDroppedFile,
  isValidExtension,
  setIsValidExtension,
  setIsDisabled,
  handleDrop,
  handleAddFiles,
  isFileOverBox,
  setIsFileOverBox,
  dropBoxLabel,
  allowedFileTypes,
  isMultipleFilesAllowed,
  dragDropIconPosition,
  dragDropBoxHeight,
  subTextSize,
  dataTestId = undefined,
}) => {
  const inputRef = useRef(null);

  //   handleDragOver describes case dragging file over the drop area and allow to change background color
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsFileOverBox(true);
  };

  //   handleDragLeave describes case when user dragging file out of the drop area
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsFileOverBox(false);
  };

  //   delete file that was not saved yet, reset the states
  const handleDeleteFile = () => {
    setDroppedFile(null);
    setIsValidExtension(false);
    setIsDisabled(true);
    setIsFileOverBox(false);
  };

  return (
    <div
      className={dragDropBoxHeight}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {droppedFile
        ? (
          <div className="flex">
            {isValidExtension
              ? <CompletedTrue color={tailwindTheme.theme.extend.colors['vamos-green1']} />
              : <CompletedFalse color={tailwindTheme.theme.extend.colors['vamos-red']} />}
            <div className=" pl-1.25 pr-2.5 space-y-2 items-top">
              <div className="flex">
                <p className="line-clamp-3 break-all">{droppedFile.name}</p>
              </div>
              {!isValidExtension && <p className="text-sm items-left text-vamos-red">Invalid file type. Save your file as a <br />CSV and try again.</p>}
            </div>
            <button type="button" aria-label="delete file" onClick={handleDeleteFile} className="w-6 h-6 flex-shrink-0" data-testid={dataTestId && `delete-${dataTestId}`}>
              <Icon name="delete" />
            </button>
          </div>
        )
        : (
          <div className={`${isFileOverBox ? 'bg-vamos-green3' : 'bg-vamos-white'} border border-dashed border-vamos-gray rounded-md h-full`}>
            <div className="flex items-center justify-center flex-col space-y-1.75 h-full">
              {dragDropIconPosition === 'above label' && (
                <div
                  className="cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => inputRef.current?.click()}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      inputRef.current?.click();
                    }
                  }}
                  aria-label="Open file dialog"
                >
                  <Icon name="download_green" />
                </div>
              )}
              <div>
                {/* hidden input that allows to choose file */}
                <input
                  type="file"
                  hidden
                  id="choose-file"
                  onChange={handleAddFiles}
                  accept={allowedFileTypes}
                  aria-label={dropBoxLabel}
                  multiple={isMultipleFilesAllowed}
                  ref={inputRef}
                  data-testid={dataTestId && `choose_${dataTestId}`}
                />

                <label
                  htmlFor="choose-file"
                  className="flex items-center gap-2 text-sm font-bold text-vamos-green4 cursor-pointer"
                >
                  {dragDropIconPosition !== 'above label' && <Icon name="download_green" />}
                  {dropBoxLabel}
                </label>
              </div>
              <p className={subTextSize}>or drag and drop here</p>
            </div>
          </div>
        )}
    </div>
  );
};

DragDropFiles.propTypes = {
  droppedFile: PropTypes.object.isRequired,
  setDroppedFile: PropTypes.func.isRequired,
  isValidExtension: PropTypes.bool.isRequired,
  setIsValidExtension: PropTypes.func.isRequired,
  setIsDisabled: PropTypes.func.isRequired,
  dragDropBoxHeight: PropTypes.string.isRequired,
  dragDropIconPosition: PropTypes.string.isRequired,
  handleDrop: PropTypes.func.isRequired,
  handleAddFiles: PropTypes.func.isRequired,
  isFileOverBox: PropTypes.bool.isRequired,
  setIsFileOverBox: PropTypes.func.isRequired,
  dropBoxLabel: PropTypes.string.isRequired,
  allowedFileTypes: PropTypes.string.isRequired,
  isMultipleFilesAllowed: PropTypes.bool.isRequired,
  subTextSize: PropTypes.string.isRequired,
  dataTestId: PropTypes.string,
};

export default DragDropFiles;
